import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const FormControlWrapper = styled(FormControl)(({ theme }) => ({
  flex: 1,
  minWidth: '300px',
  maxWidth: '300px',
  width: '300px'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FF6B00',
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#FF8533',
  },
  '&.Mui-disabled': {
    backgroundColor: '#FFB74D',
    color: '#FFFFFF',
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'flex-end',
}));

const RefreshDataTab = ({
  product,
  productList,
  selectedProduct,
  selectedItems,
  selectedData,
  handleCheckboxToggle,
  handleFileInputChange,
  selectedFile,
  handleUploadFile,
  refreshTable,
  formDataTable,
  handleDeleteClick,
  handleRefreshTable,
  fileInfoColumns,
  formatFileSize,
  setSelectedFile,
  setSelectedItems,
  setSelectedData,
  module,
  moduleList,
  selectedModule,
  testCase,
  testCaseList,
  selectedTestCase,
  clearFilters,
  getData,
}) => {
  const tableColumns = [
    { name: "Testcase", id: "TestCase", width: '14%', align: "center", colspan: 1 },
    { name: "Testcase Desc", id: "Testcase Desc", width: '14%', align: "center", colspan: 1 },
    { name: "Step Number", id: "Stepnumber", width: '14%', align: "center", colspan: 1 },
    { name: "Step Description", id: "stepdescription", width: '14%', align: "center", colspan: 1 },
    { name: "Filename", id: "Filename", width: '15%', align: "center", colspan: 1 },
    { name: "Filefield", id: "Filefield", width: '15%', align: "center", colspan: 1 },
    { name: "Status", id: "Status", width: '14%', align: "center", colspan: 1 },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="#333333">
          Refresh Test Data Files
        </Typography>
        <Box>
          <IconButton onClick={handleRefreshTable}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleDeleteClick} disabled={selectedItems.length === 0}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControlWrapper>
          <InputLabel>Product</InputLabel>
          <Select
            value={product}
            onChange={selectedProduct}
            label="Product"
          >
            {productList.map((item) => (
              <MenuItem key={item.idproducts} value={item.idproducts}>
                {item.productname}
              </MenuItem>
            ))}
          </Select>
        </FormControlWrapper>

        {product && (
          <FormControlWrapper>
            <InputLabel>Module</InputLabel>
            <Select
              value={module}
              onChange={selectedModule}
              label="Module"
            >
              {moduleList.map((item) => (
                <MenuItem key={item.idmodules} value={item.idmodules}>
                  {item.modulename}
                </MenuItem>
              ))}
            </Select>
          </FormControlWrapper>
        )}

        {module && (
          <FormControlWrapper>
            <InputLabel>Test Case</InputLabel>
            <Select
              value={testCase}
              onChange={selectedTestCase}
              label="Test Case"
            >
              {testCaseList.map((item) => (
                <MenuItem key={item.idtest_case} value={item.idtest_case}>
                  {item.Test_Case}
                </MenuItem>
              ))}
            </Select>
          </FormControlWrapper>
        )}

        {(testCase !== "" || module !== "") && (
          <IconButton
            onClick={clearFilters}
            sx={{ color: '#FF6B00' }}
          >
            <ClearAllIcon />
          </IconButton>
        )}

        {product && (
          <StyledButton
            onClick={() => getData(product)}
          >
            Get
          </StyledButton>
        )}

        <ButtonContainer>
          {!selectedFile.filename ? (
            <Box>
              <input
                accept=".zip"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileInputChange}
              />
              <label htmlFor="file-upload">
                <StyledButton
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={!product}
                >
                  Choose File to Refresh Data
                </StyledButton>
              </label>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <StyledButton
                onClick={handleUploadFile}
                startIcon={<CloudUploadIcon />}
              >
                Refresh - {selectedFile.filename}
              </StyledButton>
              <IconButton 
                onClick={() => setSelectedFile({ file: "", filename: "" })}
                size="small"
                sx={{ color: '#FF6B00' }}
              >
                <ClearAllIcon />
              </IconButton>
            </Box>
          )}
        </ButtonContainer>
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell 
                padding="checkbox" 
                sx={{ 
                  width: '5%',
                  minWidth: '5%',
                  maxWidth: '5%'
                }}
              >
                <Checkbox
                  indeterminate={selectedItems.length > 0 && selectedItems.length < formDataTable.length}
                  checked={selectedItems.length === formDataTable.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedItems(formDataTable);
                      setSelectedData(formDataTable.map(item => item.FileName));
                    } else {
                      setSelectedItems([]);
                      setSelectedData([]);
                    }
                  }}
                  sx={{
                    color: '#FF6B00',
                    '&.Mui-checked': {
                      color: '#FF6B00',
                    },
                    '&.Mui-indeterminate': {
                      color: '#FF6B00',
                      '& .MuiSvgIcon-root': {
                        color: '#FF6B00',
                      }
                    }
                  }}
                />
              </TableCell>
              {tableColumns.map((column) => (
                <TableCell 
                  key={column.id} 
                  align={column.align}
                  colSpan={column.colspan}
                  sx={{ 
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width,
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5'
                  }}
                >
                  {column.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {formDataTable.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell 
                  padding="checkbox" 
                  sx={{ 
                    width: '5%',
                    minWidth: '5%',
                    maxWidth: '5%'
                  }}
                >
                  <Checkbox
                    checked={selectedData.includes(row.FileName)}
                    onChange={(e) => handleCheckboxToggle(e, row)}
                    sx={{
                      color: '#FF6B00',
                      '&.Mui-checked': {
                        color: '#FF6B00',
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="center">{row.TestcaseName}</TableCell>
                <TableCell align="center">{row.TestCaseDesc}</TableCell>
                <TableCell align="center">{row.IdTeststep}</TableCell>
                <TableCell align="center">{row.TeststepDesc}</TableCell>
                <TableCell align="center">{row.FileName}</TableCell>
                <TableCell align="center">{row.FileField}</TableCell>
                <TableCell align="center">
                  {row.status === "Active" ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CheckCircleOutlineIcon color="disabled" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default RefreshDataTab; 